import React, { useState, useEffect } from 'react';
import { fetchDroneFlyingDetails } from "../Apis/AdminApis";
import './DroneFlyingDetails.css';
import Sidebar from "../Sidebar/Sidebar";

const DroneFlyingDetails = () => {
  const [telemetry, setTelemetry] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllTelemetry, setShowAllTelemetry] = useState({});

  useEffect(() => {
    loadTelemetry();
  }, []);

  const loadTelemetry = async () => {
    try {
      setLoading(true);
      const data = await fetchDroneFlyingDetails();
      setTelemetry(data.vehicles || []);
      setError(null);
    } catch (err) {
      setError('Failed to load drone flying details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const calculateHDOPQuality = (hdop) => {
    if (hdop < 1) return { label: 'Excellent', color: '#28a745', className: 'dfd-hdop-excellent' };
    if (hdop < 2) return { label: 'Good', color: '#17a2b8', className: 'dfd-hdop-good' };
    if (hdop < 5) return { label: 'Moderate', color: '#ffc107', className: 'dfd-hdop-moderate' };
    return { label: 'Poor', color: '#dc3545', className: 'dfd-hdop-poor' };
  };

  const toggleShowAllTelemetry = (vehicleId) => {
    setShowAllTelemetry(prev => ({
      ...prev,
      [vehicleId]: !prev[vehicleId]
    }));
  };

  return (
    <div className="dashboard-wrapper">
       <Sidebar />
       <div className="dashboard-content">
      <div className="dashboard-header">
        <div className="dfd-header-content">
          <h2 className="page-title">Drone Flying Details</h2>
          <p className="dfd-subtitle">Real-time telemetry and attitude data for all vehicles</p>
        </div>
        <button 
          className="dfd-refresh-btn"
          onClick={loadTelemetry}
          disabled={loading}
        >
          <span className="dfd-refresh-icon">🔄</span>
          {loading ? 'Syncing...' : 'Refresh Data'}
        </button>
      </div>

      {error && (
        <div className="dfd-error">
          <span className="dfd-error-icon">⚠</span>
          <div className="dfd-error-content">
            <strong>Connection Error</strong>
            <p>{error}</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="dfd-loading">
          <div className="dfd-spinner"></div>
          <p className="dfd-loading-text">Acquiring telemetry data...</p>
        </div>
      ) : telemetry.length === 0 ? (
        <div className="dfd-empty">
          <div className="dfd-empty-icon">🛸</div>
          <h3 className="dfd-empty-title">No Telemetry Data Available</h3>
          <p className="dfd-empty-text">No drone telemetry data has been recorded yet.</p>
        </div>
      ) : (
        <div className="dfd-vehicles-list">
          {telemetry.map((vehicle, idx) => (
            <div key={vehicle.vehicle_id || idx} className="dfd-vehicle-section">
              <div className="dfd-vehicle-header">
                <div className="dfd-vehicle-info">
                  <div className="dfd-vehicle-icon"><img
    src={require("../images/drone image.png")}
    alt="Drone"
    className="drone-icon"
  /></div>
                  <div className="dfd-vehicle-details">
                    <h3 className="dfd-vehicle-id">
                      Drone {vehicle.vehicle_id || 'Unknown'}
                    </h3>
                    <div className="dfd-vehicle-stats">
                      <span className="dfd-stat">
                        <span className="dfd-stat-label">Telemetry Records:</span>
                        <span className="dfd-stat-value">{vehicle.count || 0}</span>
                      </span>
                      <span className="dfd-stat">
                        <span className="dfd-stat-label">Pilot ID:</span>
                        <span className="dfd-stat-value">
                          {vehicle.telemetry_data?.[0]?.pilot_id || 'N/A'}
                        </span>
                      </span>
                      <span className="dfd-stat">
                        <span className="dfd-stat-label">Mission ID:</span>
                        <span className="dfd-stat-value">
                          {vehicle.telemetry_data?.[0]?.mission_id || 'N/A'}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="dfd-telemetry-section">
                <div className="dfd-telemetry-table-header">
                  <span className="dfd-table-header-cell">Time</span>
                  <span className="dfd-table-header-cell">Roll</span>
                  <span className="dfd-table-header-cell">Pitch</span>
                  <span className="dfd-table-header-cell">Yaw</span>
                  <span className="dfd-table-header-cell">HDOP</span>
                  <span className="dfd-table-header-cell">Status</span>
                </div>
                
                {(showAllTelemetry[vehicle.vehicle_id] 
                  ? vehicle.telemetry_data 
                  : vehicle.telemetry_data?.slice(0, 5)
                )?.map((data, dataIdx) => {
                  const hdopQuality = calculateHDOPQuality(data.hdop);
                  return (
                    <div key={data.id || dataIdx} className="dfd-telemetry-row">
                      <span className="dfd-telemetry-cell dfd-time-cell">
                        {formatDateTime(data.time_and_date)}
                      </span>
                      <span className="dfd-telemetry-cell">
                        <span className="dfd-value">{data.roll?.toFixed(2) || '0.00'}°</span>
                      </span>
                      <span className="dfd-telemetry-cell">
                        <span className="dfd-value">{data.pitch?.toFixed(2) || '0.00'}°</span>
                      </span>
                      <span className="dfd-telemetry-cell">
                        <span className="dfd-value">{data.yaw?.toFixed(2) || '0.00'}°</span>
                      </span>
                      <span className="dfd-telemetry-cell">
                        <span 
                          className={`dfd-hdop-indicator ${hdopQuality.className}`}
                          style={{ color: hdopQuality.color }}
                        >
                          <span className="dfd-hdop-value">
                            {data.hdop?.toFixed(2) || 'N/A'}
                          </span>
                          <span className="dfd-hdop-label">
                            {hdopQuality.label}
                          </span>
                        </span>
                      </span>
                      <span className="dfd-telemetry-cell">
                        <span className={`dfd-status-indicator ${dataIdx === 0 ? 'dfd-status-latest' : ''}`}>
                          {dataIdx === 0 ? 'Latest' : 'Recorded'}
                        </span>
                      </span>
                    </div>
                  );
                })}
                
                {vehicle.telemetry_data?.length > 5 && (
                  <div className="dfd-view-more">
                    <button 
                      className="dfd-view-more-btn"
                      onClick={() => toggleShowAllTelemetry(vehicle.vehicle_id)}
                    >
                      {showAllTelemetry[vehicle.vehicle_id] 
                        ? `Show Less (5 of ${vehicle.telemetry_data.length})` 
                        : `Show All (${vehicle.telemetry_data.length} records)`
                      }
                      <span className="dfd-view-more-icon">
                        {showAllTelemetry[vehicle.vehicle_id] ? '▲' : '▼'}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="dfd-info-panel">
        <div className="dfd-info-item">
          <span className="dfd-info-icon">📡</span>
          <div className="dfd-info-content">
            <strong>HDOP (Horizontal Dilution of Precision) Guide:</strong>
            <p>&lt;1: Excellent | 1-2: Good | 2-5: Moderate | &gt;5: Poor - Lower values indicate better GPS accuracy</p>
          </div>
        </div>
        <div className="dfd-info-item">
          <span className="dfd-info-icon">📊</span>
          <div className="dfd-info-content">
            <strong>Telemetry Data Information:</strong>
            <p>Roll, Pitch, and Yaw are in degrees. Latest records are highlighted. Data refreshes automatically.</p>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default DroneFlyingDetails;